import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "../../../../parts/inputs/select";
import Modal from "../../../../parts/modal-new";
import ModalFooter from "../../../../parts/modals/ModalFooter";
import {
  getIntervalLabel,
  getMinimumTermLength,
} from "../../../../utils/stripe-helpers";

const getFrequencyKey = (recurring, periods) => {
  if (!recurring) return "frequencyMonth";
  const { interval, interval_count } = recurring;
  const plural = periods > 1;
  if (interval === "month") {
    return plural ? "frequencyMonths" : "frequencyMonth";
  }
  if (interval === "week") {
    return plural ? "frequencyWeeks" : "frequencyWeek";
  }
  if (interval === "year") return "frequencyAnnual";
  if (interval_count === 3) {
    return plural ? "frequencyQuarters" : "frequencyQuarter";
  }
  return plural ? "frequencyMonths" : "frequencyMonth";
};

export const MinimumTerm = ({ plan = {} }) => {
  const { t } = useTranslation("coaching-portal");
  const { id, recurring, metadata } = plan;
  const termPeriod = metadata?.minimum_term;
  const intervalLabel = getIntervalLabel(recurring, t);
  const minimumPeriodLength = getMinimumTermLength(recurring);

  const [agreement, setAgreement] = useState(!!termPeriod);
  const [hasMinimumTerm, setHasMinimumTerm] = useState(!!termPeriod);
  const [minimumTermPeriod, setMinimumTermPeriod] = useState(termPeriod || 1);

  const frequencyLabel = useMemo(() => {
    const key = getFrequencyKey(recurring, minimumTermPeriod);
    return t(`planManager.minimumTerm.${key}`);
  }, [recurring, minimumTermPeriod, t]);

  return (
    <Modal
      className="plan-modal trial min-term"
      toClear={["plan", "type"]}
      title={t("planManager.minimumTerm.editPlanTitle", {
        interval: intervalLabel,
      })}
    >
      <form
        className="body"
        action="/actions/plan-manager/add-minimum-term"
        method="POST"
        data-noreset
        id="add-minimum-term"
      >
        <input type="hidden" name="id" value={id} />
        <div className="modal-body">
          <div className="row">
            <div className="group">
              <label className="label" htmlFor="switch">
                {t("planManager.minimumTerm.addMinimumTermOptional")}
              </label>

              <input
                type="checkbox"
                id="switch"
                role="switch"
                name="hasMinimumTerm"
                checked={hasMinimumTerm}
                onChange={() => setHasMinimumTerm((state) => !state)}
              />
            </div>
          </div>
          <div className="row">
            <div className="group">
              <Select
                title={t("planManager.minimumTerm.minimumPeriod")}
                name="minimum_term"
                id="minimum_term"
                value={minimumTermPeriod}
                disabled={!hasMinimumTerm}
                onChange={(val) => setMinimumTermPeriod(val)}
                options={Array.from({ length: minimumPeriodLength }, (v, i) => {
                  return {
                    text: i + 1,
                    value: i + 1,
                  };
                })}
              />
            </div>
          </div>

          {hasMinimumTerm && (
            <span className="chip warning">
              {t("planManager.minimumTerm.subscribersWarning", {
                count: minimumTermPeriod,
                frequency: frequencyLabel,
              })}
            </span>
          )}

          {hasMinimumTerm && (
            <div className="flex items-center gap-2 mt-6">
              <input
                type="checkbox"
                id="agreement"
                name="agreeToMinimumTerm"
                checked={agreement}
                onChange={() => setAgreement((state) => !state)}
              />
              <label htmlFor="agreement">
                {t("planManager.minimumTerm.confirmMinimumTerm", {
                  planName: plan?.nickname || intervalLabel,
                })}
              </label>
            </div>
          )}
        </div>
      </form>
      <ModalFooter
        isSubmitButton
        showAltButton={false}
        onClickButton={(e) => {}}
        classNameButton="button save"
        disabled={!agreement && hasMinimumTerm}
        formId="add-minimum-term"
      />
    </Modal>
  );
};
